/**
 * RFC 0054: Image Picker Modal
 * 
 * A modal that shows:
 * 1. Gallery of existing uploaded images (multi-select)
 * 2. Drag-and-drop upload area
 * 3. File picker button
 * 4. Optional naming dialog for new uploads
 * 5. "Add to chat" button to confirm selection
 */

import { useState, useCallback, useRef, useEffect, DragEvent } from 'react'
import Modal from '../ui/Modal'
import { api, apiRequest } from '../../api/client'
import { Trans, useTranslation } from 'react-i18next'

interface UploadedAsset {
  path: string
  filename: string
  size: number
}

export interface SelectedImage {
  path: string
  name?: string
}

interface ImagePickerModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  onConfirm: (images: SelectedImage[]) => void
}

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export function ImagePickerModal({ isOpen, onClose, projectId, onConfirm }: ImagePickerModalProps) {
  const { t } = useTranslation('chat')
  const [uploads, setUploads] = useState<UploadedAsset[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  
  // Multi-select state
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set())
  
  // Name dialog state
  const [showNameDialog, setShowNameDialog] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingName, setPendingName] = useState('')
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchUploads = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await apiRequest<{ data?: UploadedAsset[] }>(
        `/projects/${projectId}/assets?type=upload`,
      )
      setUploads(data.data || [])
    } catch (err) {
      console.error('Failed to fetch uploads:', err)
      setError(t('imagePicker.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [projectId, t])

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPaths(new Set())
      setError(null)
      fetchUploads()
    }
  }, [isOpen, fetchUploads])

  // Validate file
  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return t('imagePicker.unsupportedFormat')
    }
    if (file.size > MAX_FILE_SIZE) {
      return t('imagePicker.fileTooLarge')
    }
    return null
  }, [t])

  // Handle file selection (from input or drag)
  const handleFileSelect = useCallback((file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      return
    }
    
    setError(null)
    setPendingFile(file)
    setPendingName('')
    setShowNameDialog(true)
  }, [validateFile])

  // Upload file to backend
  const uploadFile = useCallback(async (file: File, name?: string) => {
    setIsUploading(true)
    setError(null)

    try {
      const result = await api.uploadAsset(projectId, file, name, 'picker')

      const newUpload: UploadedAsset = {
        path: result.path,
        filename: result.filename,
        size: result.size,
      }
      setUploads(prev => [newUpload, ...prev])

      // Auto-select the newly uploaded file
      setSelectedPaths(prev => new Set([...prev, result.path]))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('imagePicker.uploadFailed'))
    } finally {
      setIsUploading(false)
    }
  }, [projectId, t])

  // Confirm file with name
  const handleNameConfirm = useCallback(() => {
    if (!pendingFile) return
    uploadFile(pendingFile, pendingName.trim() || undefined)
    setPendingFile(null)
    setPendingName('')
    setShowNameDialog(false)
  }, [pendingFile, pendingName, uploadFile])

  // Skip naming dialog
  const handleNameSkip = useCallback(() => {
    if (!pendingFile) return
    uploadFile(pendingFile)
    setPendingFile(null)
    setPendingName('')
    setShowNameDialog(false)
  }, [pendingFile, uploadFile])

  // Cancel upload
  const handleNameCancel = useCallback(() => {
    setPendingFile(null)
    setPendingName('')
    setShowNameDialog(false)
  }, [])

  // File input change handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    // Reset input
    e.target.value = ''
  }, [handleFileSelect])

  // Drag and drop handlers
  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  // Toggle selection of existing upload
  const toggleSelection = useCallback((path: string) => {
    setSelectedPaths(prev => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }, [])

  // Confirm selection and add to chat
  const handleConfirm = useCallback(() => {
    const selectedImages: SelectedImage[] = []
    for (const path of selectedPaths) {
      const upload = uploads.find(u => u.path === path)
      if (upload) {
        // Extract name from filename (without extension)
        const name = upload.filename.replace(/\.[^.]+$/, '')
        selectedImages.push({ path, name })
      }
    }
    onConfirm(selectedImages)
    onClose()
  }, [selectedPaths, uploads, onConfirm, onClose])

  const hasSelection = selectedPaths.size > 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('imagePicker.title')} size="md">
      <div className="space-y-4">
        {/* Error message */}
        {error && (
          <div className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-sm text-red-400">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-300 hover:text-foreground">&times;</button>
          </div>
        )}

        {/* Name dialog overlay */}
        {showNameDialog && pendingFile && (
          <div className="absolute inset-0 bg-surface-light rounded-lg z-10 flex flex-col p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">{t('imagePicker.nameTitle')}</h3>
            <div className="flex-1 flex flex-col items-center justify-center space-y-4">
              <img 
                src={URL.createObjectURL(pendingFile)} 
                alt={t('imagePicker.preview')}
                className="w-32 h-32 object-cover rounded-lg border border-white/10"
              />
              <input
                type="text"
                placeholder={t('imagePicker.namePlaceholder')}
                value={pendingName}
                onChange={(e) => setPendingName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNameConfirm()}
                className="w-full max-w-sm px-3 py-2 bg-surface-1 border border-white/10 rounded-lg text-foreground placeholder-white/40 focus:outline-none focus:border-border"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={handleNameCancel}
                className="px-4 py-2 text-sm text-foreground/70 hover:text-foreground transition-colors"
              >
                {t('imagePicker.cancel')}
              </button>
              <button
                onClick={handleNameSkip}
                disabled={isUploading}
                className="px-4 py-2 text-sm text-foreground/70 hover:text-foreground transition-colors disabled:opacity-50"
              >
                {isUploading ? t('imagePicker.uploading') : t('imagePicker.skipName')}
              </button>
              <button
                onClick={handleNameConfirm}
                disabled={isUploading}
                className="px-4 py-2 text-sm bg-primary text-foreground rounded-lg hover:bg-primary-light transition-colors disabled:opacity-50"
              >
                {isUploading ? t('imagePicker.uploading') : t('imagePicker.upload')}
              </button>
            </div>
          </div>
        )}

        {/* Existing uploads gallery */}
        {uploads.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-foreground/70 mb-2">
              {t('imagePicker.yourUploads')} {hasSelection && <span className="text-primary">{t('imagePicker.selectedCount', { count: selectedPaths.size })}</span>}
            </h3>
            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {uploads.map((upload) => {
                const isSelected = selectedPaths.has(upload.path)
                return (
                  <button
                    key={upload.path}
                    onClick={() => toggleSelection(upload.path)}
                    className={`
                      relative aspect-square rounded-lg overflow-hidden border-2 transition-all
                      ${isSelected 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-white/10 hover:border-border'
                      }
                    `}
                  >
                    <img
                      src={api.getAssetUrl(projectId, upload.path)}
                      alt={upload.filename}
                      className="w-full h-full object-cover"
                    />
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    {/* Filename tooltip on hover */}
                    <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/70 text-xxs text-foreground truncate opacity-0 hover:opacity-100 transition-opacity">
                      {upload.filename}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Drag and drop area */}
        <div
          onDragOver={isUploading ? undefined : handleDragOver}
          onDragLeave={isUploading ? undefined : handleDragLeave}
          onDrop={isUploading ? undefined : handleDrop}
          className={`
            relative border-2 border-dashed rounded-md p-6 text-center transition-colors
            ${isUploading
              ? 'border-primary/50 bg-primary/5'
              : isDragging 
                ? 'border-primary bg-primary/10' 
                : 'border-border hover:border-white/40'
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleInputChange}
            className="hidden"
            disabled={isUploading}
          />
          
          {isUploading ? (
            <div className="space-y-2">
              <div className="w-10 h-10 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-sm text-primary">{t('imagePicker.uploadingImage')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="w-10 h-10 mx-auto rounded-full bg-surface-1 flex items-center justify-center">
                <svg className="w-5 h-5 text-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              
              <div>
                <p className="text-sm text-foreground/70">
                  <Trans
                    i18nKey="imagePicker.dragAndDrop"
                    t={t}
                    components={{
                      browseButton: <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-primary hover:underline"
                      />,
                    }}
                  />
                </p>
                <p className="text-xs font-medium text-text-muted mt-1">
                  {t('imagePicker.fileTypes')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <button
            onClick={onClose}
            disabled={isUploading}
            className="px-4 py-2 text-sm text-foreground/70 hover:text-foreground transition-colors disabled:opacity-50"
          >
            {t('imagePicker.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasSelection || isUploading}
            className={`
              px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2
              ${hasSelection && !isUploading
                ? 'bg-primary text-foreground hover:bg-primary-light' 
                : 'bg-surface-2 text-text-muted cursor-not-allowed'
              }
            `}
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t('imagePicker.uploading')}
              </>
            ) : (
              hasSelection ? t('imagePicker.addToChatCount', { count: selectedPaths.size }) : t('imagePicker.addToChat')
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default ImagePickerModal
